/**
 * cart-functions.js
 *
 * Generic cart utility functions exposed on `window` for use anywhere in the theme.
 *
 * Functions:
 *   - addToCart(item)         — add a single line item (no file support)
 *   - addManyToCart(items)    — add multiple line items in one request
 *   - nukeCart()              — empty the entire cart and reload the page
 *   - updateCartCount()       — refresh [data-cart-count] elements from /cart.js
 */

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Returns the Shopify cart root path (handles sub-folder storefronts).
 * @returns {string}
 */
function getCartRoot() {
  return (window.Theme && window.Theme.routes && window.Theme.routes.root)
    ? window.Theme.routes.root
    : '/';
}

/**
 * Remove null-ish / empty properties and stringify values.
 * Shopify stores line-item properties as strings.
 *
 * @param {Record<string, any> | null | undefined} properties
 * @returns {Record<string, string> | undefined}
 */
function normalizeProperties(properties) {
  if (!properties || typeof properties !== 'object') return undefined;

  const out = {};
  for (const [k, v] of Object.entries(properties)) {
    if (!k) continue;
    if (v === null || v === undefined || v === '') continue;
    out[String(k)] = String(v);
  }
  return Object.keys(out).length ? out : undefined;
}

// ---------------------------------------------------------------------------
// addToCart
// ---------------------------------------------------------------------------

/**
 * Add a single line item to the cart.
 *
 * @param {{ id: number|string, quantity?: number, properties?: Record<string, any>, selling_plan?: number }} item
 * @returns {Promise<object>} Shopify line-item response
 *
 * @example
 * await window.addToCart({ id: 12345678, quantity: 2, properties: { colour: 'red' } });
 */
async function addToCart(item) {
  if (!item || !item.id) throw new Error('addToCart: item.id is required');

  const root = getCartRoot();

  const payload = {
    id: item.id,
    quantity: item.quantity ?? 1,
  };

  const props = normalizeProperties(item.properties);
  if (props) payload.properties = props;

  if (item.selling_plan != null && item.selling_plan !== '') {
    payload.selling_plan = item.selling_plan;
  }

  const response = await fetch(root + 'cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(payload),
  });

  const raw = await response.text();
  let data = null;
  try {
    data = raw ? JSON.parse(raw) : null;
  } catch (_) {
    throw new Error(`addToCart: response is not valid JSON (status ${response.status})`);
  }

  if (!response.ok) {
    throw new Error(data?.description || data?.message || `addToCart failed (HTTP ${response.status})`);
  }

  window.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'addToCart', data } }));
  return data;
}

// ---------------------------------------------------------------------------
// addManyToCart
// ---------------------------------------------------------------------------

/**
 * Add multiple line items to the cart in a single request.
 *
 * @param {Array<{ id: number|string, quantity?: number|string, properties?: Record<string, any>, selling_plan?: number|string }>} items
 * @param {{ dispatchEvent?: boolean }} [options]
 * @returns {Promise<any>}
 *
 * @example
 * await window.addManyToCart([
 *   { id: 111, quantity: 1 },
 *   { id: 222, quantity: 2, properties: { engraving: 'Hello' } },
 * ]);
 */
async function addManyToCart(items, options = {}) {
  const { dispatchEvent = true } = options;

  if (!Array.isArray(items) || items.length === 0) return null;

  const root = getCartRoot();

  const normalizedItems = items.map((item) => {
    if (!item || !item.id) throw new Error('addManyToCart: every item must have an id');

    const id = typeof item.id === 'number' ? item.id : parseInt(String(item.id), 10);
    if (!Number.isFinite(id)) throw new Error(`addManyToCart: invalid variant id "${item.id}"`);

    const quantity = item.quantity == null ? 1 : parseInt(String(item.quantity), 10);
    if (!Number.isFinite(quantity) || quantity <= 0) {
      throw new Error(`addManyToCart: invalid quantity for variant ${item.id}`);
    }

    const normalized = { id, quantity };

    const props = normalizeProperties(item.properties);
    if (props) normalized.properties = props;

    if (item.selling_plan != null && item.selling_plan !== '') {
      const sp = typeof item.selling_plan === 'number' ? item.selling_plan : parseInt(String(item.selling_plan), 10);
      if (Number.isFinite(sp)) normalized.selling_plan = sp;
    }

    return normalized;
  });

  const response = await fetch(root + 'cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ items: normalizedItems }),
  });

  const raw = await response.text();
  let data = null;
  try {
    data = raw ? JSON.parse(raw) : null;
  } catch (_) {
    throw new Error(`addManyToCart: response is not valid JSON (status ${response.status})`);
  }

  if (!response.ok) {
    throw new Error(data?.description || data?.message || `addManyToCart failed (HTTP ${response.status})`);
  }

  if (dispatchEvent) {
    window.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'addManyToCart', data } }));
  }

  return data;
}

// ---------------------------------------------------------------------------
// nukeCart
// ---------------------------------------------------------------------------

/**
 * Empty the entire cart, then reload the page.
 *
 * @returns {Promise<object>} Shopify cart object (empty)
 *
 * @example
 * <button onclick="window.nukeCart()">Empty cart</button>
 */
async function nukeCart() {
  const root = getCartRoot();

  const response = await fetch(root + 'cart/clear.js', {
    method: 'POST',
    headers: { Accept: 'application/json' },
  });

  const raw = await response.text();
  let data = null;
  try {
    data = raw ? JSON.parse(raw) : null;
  } catch (_) {
    throw new Error(`nukeCart: response is not valid JSON (status ${response.status})`);
  }

  if (!response.ok) {
    throw new Error(data?.description || data?.message || `nukeCart failed (HTTP ${response.status})`);
  }

  window.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'nukeCart', data } }));
  window.location.reload();
  return data;
}

// ---------------------------------------------------------------------------
// updateCartCount
// ---------------------------------------------------------------------------

/**
 * Fetch the current cart and update all [data-cart-count] elements on the page.
 *
 * @returns {Promise<void>}
 */
async function updateCartCount() {
  try {
    const response = await fetch('/cart.js');
    const cartData = await response.json();
    const count = cartData.item_count ?? 0;

    // Update plain text count elements (e.g. [data-cart-count])
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
    });

    // Update the header cart bubble
    const bubble = document.querySelector('._cart-bubble');
    const bubbleCount = document.querySelector('._cart-bubble__count');
    if (bubble && bubbleCount) {
      bubbleCount.textContent = count > 99 ? '99+' : String(count);
      bubble.classList.toggle('_cart-bubble--hidden', count === 0);
      // Toggle donut mask on the cart icon wrapper
      const wrapper = document.querySelector('._cart-icon-wrapper');
      if (wrapper) wrapper.classList.toggle('_cart-icon-wrapper--has-items', count > 0);
    }
  } catch (error) {
    console.error('updateCartCount: failed to fetch cart', error);
  }
}

// ---------------------------------------------------------------------------
// Expose on window
// ---------------------------------------------------------------------------

if (typeof window !== 'undefined') {
  window.addToCart = addToCart;
  window.addManyToCart = addManyToCart;
  window.nukeCart = nukeCart;
  window.updateCartCount = updateCartCount;
}
